import React from "react";

import { apiGet } from "../../api";

// Data + filter-state controller for the /webinars listing page. GETs the
// public /api/v1/webinars catalog, mirrors the rebrand pagination envelope
// ({ data, meta: { page, pages, perpage, total } }), and keeps the active
// filters reflected in the URL query string so a filtered view is shareable
// and survives back/forward navigation.
//
// The endpoint whitelists every param server-side; this hook only ever sends
// the sanitized shapes below.

const PER_PAGE = 12;

const STATUSES = ["all", "upcoming", "past"];
const TYPES = ["free", "general", "special"];
const SORTS = ["date_desc", "date_asc", "title_asc", "capacity"];

export const DEFAULT_FILTERS = {
  q: "",
  status: "all",
  type: "",
  // Tema / Nama Tipe — the managed vocabularies. Values are slugs, validated
  // against the option lists the server ships as Inertia props.
  theme: "",
  type_name: "",
  has_recording: false,
  registered: false,
  date_from: "",
  date_to: "",
  sort: "date_desc",
  page: 1,
};

// URL params for the shareable address bar (per_page is a client concern and
// is omitted here; the fetch adds it separately).
function toUrlParams(filters) {
  const p = new URLSearchParams();
  if (filters.q) p.set("q", filters.q);
  if (filters.status && filters.status !== "all")
    p.set("status", filters.status);
  if (filters.type) p.set("type", filters.type);
  if (filters.theme) p.set("theme", filters.theme);
  if (filters.type_name) p.set("type_name", filters.type_name);
  if (filters.has_recording) p.set("has_recording", "true");
  if (filters.registered) p.set("registered", "true");
  if (filters.date_from) p.set("date_from", filters.date_from);
  if (filters.date_to) p.set("date_to", filters.date_to);
  if (filters.sort && filters.sort !== "date_desc") p.set("sort", filters.sort);
  if (filters.page && filters.page > 1) p.set("page", String(filters.page));
  return p;
}

function fromUrl() {
  if (typeof window === "undefined") return { ...DEFAULT_FILTERS };
  const p = new URLSearchParams(window.location.search);
  const page = parseInt(p.get("page"), 10);
  return {
    q: p.get("q") || "",
    status: STATUSES.includes(p.get("status")) ? p.get("status") : "all",
    type: TYPES.includes(p.get("type")) ? p.get("type") : "",
    // Slugs are free-form by nature; the server ignores unknown ones, and the
    // <select> simply shows nothing selected.
    theme: p.get("theme") || "",
    type_name: p.get("type_name") || "",
    has_recording: p.get("has_recording") === "true",
    registered: p.get("registered") === "true",
    date_from: p.get("date_from") || "",
    date_to: p.get("date_to") || "",
    sort: SORTS.includes(p.get("sort")) ? p.get("sort") : "date_desc",
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

function syncUrl(filters) {
  if (typeof window === "undefined") return;
  const qs = toUrlParams(filters).toString();
  const next = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;
  window.history.replaceState(null, "", next);
}

export default function useWebinarsIndex() {
  const [filters, setFilters] = React.useState(fromUrl);
  const [results, setResults] = React.useState([]);
  const [meta, setMeta] = React.useState({
    page: 1,
    pages: 0,
    perpage: PER_PAGE,
    total: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // `filters.q` only ever holds a COMMITTED search term, so this effect no longer
  // needs a debounce of its own. Every filter change here is a single deliberate
  // action (a radio, the sort, a page) and applies immediately; typing is debounced
  // upstream in useDebouncedSearch. The old blanket 250ms delay made clicks feel
  // laggy while still firing a request per keystroke.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    syncUrl(filters);

    const run = async () => {
      const params = toUrlParams(filters);
      params.set("per_page", String(PER_PAGE));
      try {
        const { parsedResponse, statusCode } = await apiGet(
          `/api/v1/webinars?${params.toString()}`,
        );
        if (cancelled) return;
        if (statusCode >= 400 || !parsedResponse) {
          setError(true);
          setResults([]);
          setMeta((m) => ({ ...m, pages: 0, total: 0 }));
        } else {
          setResults(parsedResponse.data ?? []);
          setMeta(
            parsedResponse.meta ?? {
              page: filters.page,
              pages: 0,
              perpage: PER_PAGE,
              total: 0,
            },
          );
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  // Any filter change resets to page 1; paging keeps the rest intact.
  const update = React.useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch, page: 1 }));
  }, []);

  // Handed to <SearchBox>, which owns the draft. Stable identity is load-bearing:
  // SearchBox rebuilds its debounce timer whenever this changes.
  const commitSearch = React.useCallback((q) => update({ q }), [update]);

  const setPage = React.useCallback((page) => {
    setFilters((f) => ({ ...f, page }));
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  // Bumped on reset so <SearchBox> clears its own draft. The parent deliberately
  // does not hold the draft, so it cannot clear it directly.
  const [searchResetToken, setSearchResetToken] = React.useState(0);

  const reset = React.useCallback(() => {
    setSearchResetToken((n) => n + 1);
    setFilters({ ...DEFAULT_FILTERS });
  }, []);

  const activeFilterCount = React.useMemo(() => {
    let n = 0;
    if (filters.q) n += 1;
    if (filters.status !== "all") n += 1;
    if (filters.type) n += 1;
    if (filters.theme) n += 1;
    if (filters.type_name) n += 1;
    if (filters.has_recording) n += 1;
    if (filters.registered) n += 1;
    if (filters.date_from || filters.date_to) n += 1;
    if (filters.sort !== "date_desc") n += 1;
    return n;
  }, [filters]);

  return {
    filters,
    results,
    meta,
    loading,
    error,
    update,
    setPage,
    reset,
    activeFilterCount,
    perPage: PER_PAGE,
    commitSearch,
    searchResetToken,
  };
}
